import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";

// Mock API responses for testing
const mockSendOTP = async ({ phone }: { phone: string }) => {
  return new Promise((resolve) => {
    setTimeout(() => resolve({ status: "success" }), 1000);
  });
};

const mockVerifyOTP = async ({ phone, otp }: { phone: string; otp: number }) => {
  if (otp === 123456) {
    return {
      data: {
        userId: "mock-user-id",
        roleId: 8, // Teacher role
        accessToken: "mock-token",
      },
    };
  }
  throw new Error("Invalid OTP");
};

const LoginNew: React.FC = () => {
  const [mobile, setMobile] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [otpVal, setOtpVal] = useState<string[]>(new Array(6).fill(""));
  const [timer, setTimer] = useState<number>(30);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<
    "login" | "forgotPassword" | "otp" | "resetPassword"
  >("login");
  const textBase = useRef<HTMLDivElement>(null);
  const otpInputsRef = useRef<HTMLInputElement[]>([]);
  const navigate = useNavigate();

  // Timer countdown for OTP
  useEffect(() => {
    if (timer > 0 && currentSection === "otp") {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, currentSection]);

  // Focus first OTP input
  useEffect(() => {
    if (currentSection === "otp" && otpInputsRef.current[0]) {
      setTimeout(() => otpInputsRef.current[0]?.focus(), 100);
    }
  }, [currentSection]);

  // Handle OTP input changes
  const handleOtpInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;
    if (value && !/^[0-9]$/.test(value)) return;

    const newOtpVal = [...otpVal];
    newOtpVal[index] = value;
    setOtpVal(newOtpVal);

    if (textBase.current?.classList.contains("otp-error")) {
      textBase.current.classList.remove("otp-error");
    }

    if (value && index < 5) {
      setTimeout(() => otpInputsRef.current[index + 1]?.focus(), 0);
    }

    if (newOtpVal.every((val) => val !== "") && newOtpVal.length === 6) {
      setTimeout(() => validateOtp(newOtpVal), 100);
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      const newOtpVal = [...otpVal];
      if (newOtpVal[index]) {
        newOtpVal[index] = "";
        setOtpVal(newOtpVal);
      } else if (index > 0) {
        newOtpVal[index - 1] = "";
        setOtpVal(newOtpVal);
        setTimeout(() => otpInputsRef.current[index - 1]?.focus(), 0);
      }
    }
  };

  // Handle Send OTP
  const handleSendOTP = async () => {
    if (mobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number");
      return;
    }
    try {
      setIsLoading(true);
      await mockSendOTP({ phone: mobile });
      setCurrentSection("otp");
      setTimer(30);
      setCanResend(false);
      setOtpVal(new Array(6).fill(""));
    } catch (error: any) {
      alert(error.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    try {
      setIsLoading(true);
      await mockSendOTP({ phone: mobile });
      setTimer(30);
      setCanResend(false);
      setOtpVal(new Array(6).fill(""));
      if (textBase.current?.classList.contains("otp-error")) {
        textBase.current.classList.remove("otp-error");
      }
      alert("OTP has been resent successfully!");
    } catch (error: any) {
      alert(error.message || "Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Validate OTP
  const validateOtp = async (otpToValidate: string[]) => {
    try {
      setIsLoading(true);
      const enteredOtp = parseInt(otpToValidate.join(""));
      await mockVerifyOTP({ phone: mobile, otp: enteredOtp });
      setCurrentSection("resetPassword");
    } catch (error: any) {
      if (textBase.current) {
        textBase.current.classList.add("otp-error");
      }
      alert(error.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = () => {
    if (newPassword.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    try {
      setIsLoading(true);
      setTimeout(() => {
        navigate("/teacher/dashboard");
        setIsLoading(false);
      }, 1000);
    } catch (error: any) {
      alert(error.message || "Failed to reset password");
    }
  };

  return (
    <div className="container-fluid">
      <div className="w-100 position-relative flex-wrap d-block vh-100">
        <div className="row vh-100">
          <div className="col-lg-6 col-md-6 col-sm-12 d-none d-md-block">
            <div className="login-bg">
              <div className="text-center">
                {/* Placeholder for background logo */}
              </div>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12 col-12 mx-auto p-2 p-sm-4 d-flex align-items-center justify-content-center">
            <div className="row w-100">
              <div className="col-12 mx-auto p-2 p-sm-4 d-flex align-items-center justify-content-center">
                <form className="w-100" onSubmit={(e) => e.preventDefault()}>
                  <div className="text-center mb-2">
                    <ImageWithBasePath
                      src="assets/img/authentication/PM-Logo.svg"
                      className="img-fluid"
                      alt="Logo"
                      width={80}
                    />
                  </div>

                  {currentSection === "login" && (
                    <div>
                      <h2 className="mt-2 mb-2 text-center">Welcome</h2>
                      <p className="mb-4 text-center">Please sign in to continue</p>
                      <label className="form-label">Mobile Number</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">
                          <i className="ti ti-phone" />
                        </span>
                        <input
                          type="tel"
                          className="form-control"
                          value={mobile}
                          onChange={(e) =>
                            setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                          }
                          maxLength={10}
                          placeholder="Enter 10-digit mobile number"
                          disabled={isLoading}
                        />
                      </div>
                      <label className="form-label">Password</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">
                          <i className="ti ti-lock" />
                        </span>
                        <input
                          type="password"
                          className="form-control"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter password"
                          disabled={isLoading}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary w-100 mb-3"
                        onClick={() => alert("Login not implemented")}
                        disabled={isLoading}
                      >
                        {isLoading ? "Loading..." : "Sign In"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-link w-100"
                        onClick={() => setCurrentSection("forgotPassword")}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}

                  {currentSection === "forgotPassword" && (
                    <div>
                      <h2 className="mt-2 mb-2 text-center">Forgot Password</h2>
                      <p className="mb-4 text-center">Enter your mobile number to receive OTP</p>
                      <label className="form-label">Mobile Number</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">
                          <i className="ti ti-phone" />
                        </span>
                        <input
                          type="tel"
                          className="form-control"
                          value={mobile}
                          onChange={(e) =>
                            setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                          }
                          maxLength={10}
                          placeholder="Enter 10-digit mobile number"
                          disabled={isLoading}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleSendOTP}
                        disabled={mobile.length !== 10 || isLoading}
                      >
                        {isLoading ? "Sending..." : "Send OTP"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-link w-100 mt-2"
                        onClick={() => setCurrentSection("login")}
                      >
                        Back to Login
                      </button>
                    </div>
                  )}

                  {currentSection === "otp" && (
                    <div>
                      <h2 className="mb-2 text-center">Verification</h2>
                      <p className="mb-5 text-center">
                        Please enter the OTP sent to +91 {mobile}
                      </p>
                      <div className="input-group mb-3 position-relative">
                        <div className="otp-base d-flex justify-content-center gap-2" ref={textBase}>
                          {Array.from({ length: 6 }, (_, index) => (
                            <input
                              key={index}
                              type="text"
                              maxLength={1}
                              className="otp-input form-control"
                              value={otpVal[index] || ""}
                              onChange={(e) => handleOtpInputChange(e, index)}
                              onKeyDown={(e) => handleOtpKeyDown(e, index)}
                              ref={(el) => {
                                if (el) otpInputsRef.current[index] = el;
                              }}
                              disabled={isLoading}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-center mb-3">
                        {timer > 0 ? `Resend OTP in ${timer}s` : "You can now resend OTP"}
                      </p>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-secondary w-100"
                          onClick={handleResendOTP}
                          disabled={!canResend || isLoading}
                        >
                          {isLoading ? "Sending..." : "Resend OTP"}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link w-100 mt-2"
                        onClick={() => {
                          setCurrentSection("forgotPassword");
                          setOtpVal(new Array(6).fill(""));
                          setTimer(30);
                          setCanResend(false);
                        }}
                      >
                        Change Mobile Number
                      </button>
                    </div>
                  )}

                  {currentSection === "resetPassword" && (
                    <div>
                      <h2 className="mb-2 text-center">Reset Password</h2>
                      <p className="mb-5 text-center">Enter your new password</p>
                      <label className="form-label">New Password</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">
                          <i className="ti ti-lock" />
                        </span>
                        <input
                          type="password"
                          className="form-control"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password"
                          disabled={isLoading}
                        />
                      </div>
                      <label className="form-label">Confirm Password</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">
                          <i className="ti ti-lock" />
                        </span>
                        <input
                          type="password"
                          className="form-control"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-secondary w-50"
                          onClick={() => setCurrentSection("forgotPassword")}
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary w-50"
                          onClick={handleResetPassword}
                          disabled={isLoading}
                        >
                          {isLoading ? "Submitting..." : "Confirm"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="text-center mt-3">
                    <ImageWithBasePath
                      src="assets/img/authentication/power.svg"
                      className="img-fluid"
                      alt="Logo"
                      width={60}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .otp-input {
          width: 32px;
          height: 32px;
          text-align: center;
          margin: 0 2px;
          font-size: 14px;
        }
        .otp-error .otp-input {
          border-color: #dc3545;
        }
        .otp-base {
          flex-wrap: nowrap !important;
        }
        @media (max-width: 576px) {
          .otp-input {
            width: 28px;
            height: 28px;
            margin: 0 1px;
            font-size: 12px;
          }
          .otp-base {
            gap: 4px;
            flex-wrap: nowrap;
          }
          .form-control, .input-group-text {
            font-size: 14px;
            padding: 6px;
          }
          .btn {
            font-size: 14px;
            padding: 8px;
          }
          h2 {
            font-size: 1.5rem;
          }
          p {
            font-size: 0.875rem;
          }
        }
        @media (max-width: 320px) {
          .otp-input {
            width: 22px;
            height: 22px;
            margin: 0 1px;
            font-size: 10px;
          }
          .otp-base {
            gap: 2px;
            flex-wrap: nowrap;
          }
          .form-control, .input-group-text {
            font-size: 12px;
            padding: 4px;
          }
          .btn {
            font-size: 12px;
            padding: 6px;
          }
          h2 {
            font-size: 1.25rem;
          }
          p {
            font-size: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginNew;